import React from 'react'
import styled from 'styled-components'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import Header from '../../../../components/Header/Header'
import Clock from '../../../../components/Clock/Clock'
import HeaderButton from '../../../../components/Header/HeaderButton'
import HeaderTitle from '../../../../components/Header/HeaderTitle'
import MediaQuery from 'react-responsive'
import {useUIDispatch} from '../../../../context/UIContext'
import {useViewFetch} from '../../../../utils/fetch'
import {fetchServiceRequestDetails} from '../../../../api/servicerequest'
import Image from '../../../Detail/Image/Image'
import {serviceRequestStatusColor} from '../../../../utils/helpers'
import ServiceRequestDetailProgressBar from './ServiceRequestDetailProgressBar'
import {getServiceRequestVisibleFields} from '../../../../utils/servicerequests'
import Spinner from '../../../../components/Spinner/Spinner'

const Status = ({color, status}) => (
  <div>
    <StatusIndicator color={color} />
    <StatusLabel color={color}>{status}</StatusLabel>
  </div>
)

const StatusIndicator = styled.div`
  display: inline-block;
  border-radius: 50%;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  background-color: ${props => props.color};
`

const StatusLabel = styled.span`
  font-size: small;
  font-weight: bold;
  color: ${props => props.color};
`

const VisibleFields = ({fields, visibleFields, localeState}) => {
  const {blName, flId, rmId, location, eqId} = fields
  const {hasLocation, hasSpecificLocation, hasEquipment} = visibleFields
  return (
    <Fields>
      {hasLocation && (
        <div>
          <Field>
            <FieldLabel>{_L({key: 'Building', localeState})}</FieldLabel>
            <FieldValue>{blName}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{_L({key: 'Floor', localeState})}</FieldLabel>
            <FieldValue>{flId}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{_L({key: 'Location', localeState})}</FieldLabel>
            <FieldValue>{rmId}</FieldValue>
          </Field>
        </div>
      )}
      {hasSpecificLocation && (
        <Field>
          <FieldLabel>{_L({key: 'Specific location', localeState})}</FieldLabel>
          <FieldValue>{location}</FieldValue>
        </Field>
      )}
      {hasEquipment && (
        <Field>
          <FieldLabel>{_L({key: 'Equipment code', localeState})}</FieldLabel>
          <FieldValue>{eqId}</FieldValue>
        </Field>
      )}
    </Fields>
  )
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
`

const Field = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0 10px 0;
`
const FieldLabel = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  color: #a4a9b7;
`
const FieldValue = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  color: #000000;
`

const Documents = ({documents, localeState}) => {
  const {doc1Url, doc2Url, doc3Url, doc4Url} = documents
  return (
    <DocumentContent>
      <FieldLabel style={{paddingBottom: '17px'}}>
        {_L({key: 'Attachments', localeState})}
      </FieldLabel>
      <PhotoContainer>
        <StyledImage>
          <Image
            url={doc1Url}
            placeholder={false}
            imageType="request"
            style={{borderRadius: '10px'}}
          />
        </StyledImage>
        <StyledImage>
          <Image
            url={doc2Url}
            placeholder={false}
            imageType="request"
            style={{borderRadius: '10px'}}
          />
        </StyledImage>
        <StyledImage>
          <Image
            url={doc3Url}
            placeholder={false}
            imageType="request"
            style={{borderRadius: '10px'}}
          />
        </StyledImage>
        <StyledImage>
          <Image
            url={doc4Url}
            placeholder={false}
            imageType="request"
            style={{borderRadius: '10px'}}
          />
        </StyledImage>
      </PhotoContainer>
    </DocumentContent>
  )
}

const DocumentContent = styled.div`
  margin-top: 20px;
  border-radius: 12px;
  background-color: #ffffff;
  padding-top: 9px;
  box-sizing: border-box;
  border-top: 1px solid #eff0f4;

  @media (max-width: 420px) {
    margin: 0;
  }
`

const PhotoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  @media (max-width: 420px) {
    justify-content: flex-start;
  }
`
const StyledImage = styled.div`
  width: 94px;
  height: 94px;
  @media (max-width: 420px) {
    margin: 4px auto 4px 0;
  }
`

const ServiceRequestDetail = ({parentPage, activityLogId}) => {
  const localeState = useLocaleState()
  const uiDispatch = useUIDispatch()

  const {data: request, isLoading} = useViewFetch({
    api: fetchServiceRequestDetails,
    parameters: {activityLogId},
    initialState: {
      activitytypeHideFields: null
    },
    onChangeOf: [activityLogId]
  })

  const {
    probType,
    status,
    statusFormat,
    description,
    blName,
    flId,
    rmId,
    location,
    eqId,
    probTypeDescription,
    activitytypeTitle,
    activitytypeHideFields: hideFields,
    doc1Url,
    doc2Url,
    doc3Url,
    doc4Url
  } = request
  const {language} = localeState
  const visibleFields = getServiceRequestVisibleFields(hideFields)
  const statusText = status === 'IN PROGRESS' ? 'APPROVED' : status
  const color = serviceRequestStatusColor[statusText]
  const requestProbType = probType
    ? language === 'en'
      ? probTypeDescription || probType
      : activitytypeTitle
    : activitytypeTitle
  const subtitle = _L({key: 'Request', localeState}) + ' #' + activityLogId
  const fields = {blName, flId, rmId, location, eqId}
  const documents = {doc1Url, doc2Url, doc3Url, doc4Url}
  const {hasDoc1, hasDoc2, hasDoc3, hasDoc4} = visibleFields
  const hasDocuments = hasDoc1 || hasDoc2 || hasDoc3 || hasDoc4

  const onCancel = () => {
    uiDispatch({
      type: 'setContent',
      payload: {content: parentPage}
    })
  }

  return (
    <Container>
      <MediaQuery maxWidth={420}>
        {matches => (
          <Header
            leftContent={
              matches ? (
                <HeaderTitle title={requestProbType} subtitle={subtitle} />
              ) : (
                <Clock />
              )
            }
            centerContent={
              matches ? (
                ''
              ) : (
                <HeaderTitle title={requestProbType} subtitle={subtitle} />
              )
            }
            rightContent={
              <HeaderButton
                label={_L({key: 'Cancel', localeState})}
                onClick={onCancel}
              />
            }
          />
        )}
      </MediaQuery>
      {isLoading ? (
        <SpinnerContainer>
          <Spinner color="#3395fe" />
        </SpinnerContainer>
      ) : (
        <Content>
          <Details>
            <DetailsContent>
              <FormContent>
                <FieldContainer>
                  <HeaderContainer>
                    <StyledTitle>
                      <Title>
                        {_L({key: 'Request #', localeState})}
                        {activityLogId}
                      </Title>
                      <Subtitle>{requestProbType}</Subtitle>
                    </StyledTitle>
                    <Status status={statusFormat} color={color} />
                  </HeaderContainer>

                  <ProgressContainer>
                    <ServiceRequestDetailProgressBar request={request} />
                  </ProgressContainer>

                  <FieldLabel>
                    {_L({key: 'Request description', localeState})}
                  </FieldLabel>
                  <DescriptionContainer>{description}</DescriptionContainer>

                  <VisibleFields
                    fields={fields}
                    visibleFields={visibleFields}
                    localeState={localeState}
                  />

                  {hasDocuments && (
                    <Documents
                      documents={documents}
                      localeState={localeState}
                    />
                  )}
                </FieldContainer>
              </FormContent>
            </DetailsContent>
          </Details>
        </Content>
      )}
    </Container>
  )
}

export default ServiceRequestDetail

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #eff0f3;
  box-sizing: border-box;
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100vw;
  height: calc(100vh - 80px);
  background-color: #eff0f3;
  box-sizing: border-box;
  overflow-y: scroll;
  padding: 34px 0 40px 0;

  @media (max-width: 420px) {
    padding: 0;
  }
`

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 4px 0 4px 0;
`

const Details = styled.div`
  width: 712px;
  position: relative;

  @media (max-width: 420px) {
    width: 100vw;
    height: calc(100vh - 80px);
    overflow-y: scroll;
    background-color: white;
  }
`

const ProgressContainer = styled.div`
  padding: 10px 0 30px 0;
`

const DetailsContent = styled.div`
  margin-top: 14px;
  margin-bottom: 24px;
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  background-color: #ffffff;
  padding: 2px 0 26px 0;

  @media (max-width: 420px) {
    margin: 0;
    border-radius: 0;
    width: 100vw;
    padding: 6px 0 100px 0;
  }
`

const FormContent = styled.div`
  margin: 20px;
  border-radius: 12px;
  background-color: #ffffff;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
  }
`

const FieldContainer = styled.div`
  padding: 24px 50px 24px 50px;

  @media (max-width: 420px) {
    padding: 18px 24px 18px 24px;
    box-sizing: border-box;
  }
`

const StyledTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  padding-bottom: 8px;
  user-select: none;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: normal;
  padding-bottom: 2px;
  user-select: none;
`

const Subtitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: #a4a9b7;
`

const DescriptionContainer = styled.div`
  border-radius: 5px;
  background-color: #f3f5fb;
  padding: 8px 12px 8px 12px;
  margin-top: 10px;
  margin-bottom: 12px;
  min-height: 50px;
  font-size: small;
`
const SpinnerContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`
